import { Col, type Form, Row, Upload } from 'antd';
import type { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import type { UploadedFile } from 'models';
import { type ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { DropboxChooser } from 'features/attachments/components/dropbox-chooser';
import { useUploadFileMutation } from 'features/files';
import { Icon } from '../../icon';
import { Loading } from '../../loading';
import { message } from '../../message';
import { Text } from '../../typography';
import { UploadButton, type UploadButtonType } from '../../upload';
import { Item } from '../item';
import { generateAttachmentsInputFileListItem } from './utilities';

const { Dragger } = Upload;

export type AttahcmentsInputUploadFile<T = UploadedFile> = {
  response: T;
} & Omit<UploadFile<T>, 'response'>;
type InputP = ComponentProps<typeof Dragger>;

type ItemP = {
  label?: string;
} & Omit<ComponentProps<typeof Form.Item>, 'children'>;

type P = {
  input?: InputP;
  item?: ItemP;
  showThirdPartyUploads?: boolean;
  uploadButtonType?: UploadButtonType;
};

const AttachmentsInputDragger = ({
  loading,
  setLoading,
  showThirdPartyUploads = true,
  uploadButtonType,
  ...rest
}: {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  showThirdPartyUploads?: boolean;
  uploadButtonType?: UploadButtonType;
} & InputP) => {
  const { t } = useTranslation();
  const { mutateAsync: uploadFile } = useUploadFileMutation();
  const [isDropboxOpened, setIsDropboxOpened] = useState(false);

  const handleChange = (info: UploadChangeParam) => {
    if (info.file.status === 'error') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      message.error(t(info.file.error.message));
    }
  };

  const onThirdPartyUploadFinish = (newFiles: UploadedFile[]) => {
    const fileList = [...(rest.fileList ?? []), ...newFiles.map((file) => generateAttachmentsInputFileListItem(file))];
    const file = fileList.at(-1);

    if (file) {
      rest.onChange?.({
        file,
        fileList,
      });
    }
  };

  const onSelectDropbox = async (data: Array<{ link: string; name: string }>) => {
    setLoading(true);

    const files = data.map((file) => ({
      name: file.name,
      url: file.link,
    }));

    const promises = files.map(async (file) => {
      try {
        return await uploadFile(file);
      } catch {
        return undefined;
      }
    });

    const uploadedFiles = await Promise.all(promises);
    onThirdPartyUploadFinish(uploadedFiles.filter((file): file is UploadedFile => file !== undefined));

    setLoading(false);
  };

  return (
    <Dragger
      customRequest={async ({ file, onError, onProgress, onSuccess }) => {
        try {
          const uploadedFile = await uploadFile({
            file,
            onProgress: (value) => onProgress?.(value),
          });

          onSuccess?.(uploadedFile);
        } catch (error) {
          onError?.(error instanceof Error ? error : new Error('Failed to upload'));
        }
      }}
      listType="picture"
      multiple
      onChange={handleChange}
      showUploadList={{
        removeIcon: (
          <Text size="large">
            <Icon icon="delete-2-line" />
          </Text>
        ),
      }}
      {...rest}
      disabled={loading}
    >
      <Row gutter={13}>
        <Col span={showThirdPartyUploads ? 18 : 24}>
          <UploadButton type={uploadButtonType} />
        </Col>
        {showThirdPartyUploads ? (
          <Col className="attachments" span={6} style={{ display: 'flex', flexWrap: 'wrap', paddingRight: 24 }}>
            <Col
              className="attachments__third-party"
              onClick={(event) => {
                event.stopPropagation();
                setIsDropboxOpened(true);
              }}
              span={24}
            >
              <DropboxChooser
                onDropdownClose={() => {
                  setIsDropboxOpened(false);
                }}
                onSelect={onSelectDropbox}
                opened={isDropboxOpened}
              >
                <div className="attachments__third-party__inner">
                  <span className="attachments__third-party__inner-text">{t('Dropbox')}</span> <Icon icon="dropbox" />
                </div>
              </DropboxChooser>
            </Col>
          </Col>
        ) : null}
      </Row>
    </Dragger>
  );
};

const AttachmentsInput = ({ input, item, showThirdPartyUploads, uploadButtonType }: P) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Item label={item?.label}>
      <Item
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        getValueProps={(value) => value}
        rules={[
          {
            message: t('Error: {{label}} is still in process of uploading', {
              label: item?.label ? t(item.label) : undefined,
            }),
            validateTrigger: 'onSubmit',
            // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
            async validator(_, value?: UploadChangeParam) {
              for (const file of value?.fileList ?? []) {
                if (file.status === 'uploading') {
                  throw new Error('Uploading in process');
                }
              }

              if (isLoading) {
                throw new Error('Uploading in process');
              }
            },
          },
        ]}
        valuePropName="fileList"
        {...item}
        noStyle
      >
        <AttachmentsInputDragger
          loading={isLoading}
          setLoading={setIsLoading}
          showThirdPartyUploads={showThirdPartyUploads}
          uploadButtonType={uploadButtonType}
          {...input}
        />
      </Item>
      {isLoading ? (
        <Col style={{ fontSize: 24, marginTop: 15, textAlign: 'center' }}>
          <Loading />
        </Col>
      ) : null}
    </Item>
  );
};

export default AttachmentsInput;
